import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import SessionDetails from '../BillersJobs/SessionDetails';
import {
  getJobInfoForPrint,
  getDemographics,
  sectionFetch
} from '../../../Apis/Jobs';
import { findPatientMoreDetails } from '../../../Apis/Doctor';
import {
  getDataOfPatient,
  getCoverages,
  getContacts,
  getAllergyIntoleranceData,
  getAdvanceDirectivesConsentsData,
  getPractitionersData
} from '../../../Apis/Patient';

// Off-screen render width (~A4 at 96dpi) used as the html2canvas capture size.
const RENDER_WIDTH = 794;

/**
 * Headless face-sheet PDF generator. Fetches all face-sheet data for a job,
 * renders the (presentational) BillersJobs SessionDetails off-screen, snapshots
 * it with html2canvas, builds a paged PDF with jsPDF, and loads the resulting
 * blob into the pre-opened `targetWindow` (opened synchronously on the click so
 * popup blockers don't intercept it). Unmounts via onComplete when done.
 */
const FaceSheetPdf = ({ jobId, targetWindow, onComplete }) => {
  const [data, setData] = useState(null);
  const captureRef = useRef(null);
  const generatedRef = useRef(false);

  const fail = message => {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.document.open();
      targetWindow.document.write(`
        <!doctype html><html><head><title>Face sheet</title>
        <style>
          html,body{margin:0;height:100%}
          body{display:flex;align-items:center;justify-content:center;
            font-family:'Krub',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
            background:#F0F2F5;color:#b3392f;padding:24px;text-align:center}
          p{font-size:14px;font-weight:500}
        </style></head>
        <body><p>${message}</p></body></html>
      `);
      targetWindow.document.close();
    }
    onComplete && onComplete();
  };

  // 1) Fetch everything the face sheet needs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const infoRes = await getJobInfoForPrint(jobId);
        const printInfoData = infoRes.data?.result || [];
        const facilityId = printInfoData[0]?.facility?.facilityId;
        const patientId = printInfoData[0]?.patientId;

        let jobName = '';
        const sectionRes = await sectionFetch(jobId).catch(() => null);
        if (sectionRes?.data?.success) jobName = sectionRes.data.jobName;

        let printMoreData = null;
        let patietSection = [];
        let coverages = [];
        let contacts = [];
        let allergies = [];
        let advanceConsents = [];
        let practitioners = [];

        if (facilityId && patientId) {
          const moreRes = await findPatientMoreDetails(
            facilityId,
            patientId
          ).catch(() => null);
          if (moreRes?.data?.success) printMoreData = moreRes.data.result;

          const effectiveDateTime = await getDemographics(jobId)
            .then(
              r =>
                r.data?.result?.find(item => item.key === 'DATE OF VISIT')
                  ?.value ?? null
            )
            .catch(() => null);

          const [
            patientRes,
            coveragesRes,
            contactsRes,
            allergyRes,
            advanceRes,
            practitionerRes
          ] = await Promise.all([
            getDataOfPatient(facilityId, patientId),
            getCoverages(facilityId, patientId, effectiveDateTime),
            getContacts(facilityId, patientId),
            getAllergyIntoleranceData(facilityId, patientId),
            getAdvanceDirectivesConsentsData(facilityId, patientId),
            getPractitionersData(facilityId, patientId)
          ]);

          if (patientRes?.data?.success) patietSection = patientRes.data.result;
          if (coveragesRes?.data?.success) coverages = coveragesRes.data.result;
          if (contactsRes?.data?.success) contacts = contactsRes.data.result;
          if (allergyRes?.data?.success) allergies = allergyRes.data.result;
          if (advanceRes?.data?.success)
            advanceConsents = advanceRes.data.result;
          if (practitionerRes?.data?.success)
            practitioners = practitionerRes.data.result;
        }

        if (cancelled) return;
        setData({
          printInfoData,
          printMoreData,
          patietSection,
          coverages,
          contacts,
          allergies,
          advanceConsents,
          practitioners,
          jobName
        });
      } catch (e) {
        console.error('Face sheet PDF fetch failed:', e);
        if (!cancelled) fail('Failed to load the face sheet.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [jobId]);

  // 2) Once rendered, snapshot to a paged PDF and open it in the target window.
  useEffect(() => {
    if (!data || generatedRef.current) return;
    generatedRef.current = true;

    const generate = async () => {
      // Let fonts/images settle before capturing.
      await new Promise(resolve => setTimeout(resolve, 600));
      const node = captureRef.current;
      if (!node) return fail('Failed to render the face sheet.');
      try {
        const canvas = await html2canvas(node, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: RENDER_WIDTH
        });
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const imgH = (canvas.height * pageW) / canvas.width;
        const imgData = canvas.toDataURL('image/jpeg', 0.95);

        let heightLeft = imgH;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
        heightLeft -= pageH;
        while (heightLeft > 0) {
          position -= pageH;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, pageW, imgH);
          heightLeft -= pageH;
        }

        const url = URL.createObjectURL(pdf.output('blob'));
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener');
        }
      } catch (e) {
        console.error('Face sheet PDF generation failed:', e);
        fail('Failed to build the face sheet PDF.');
        return;
      }
      onComplete && onComplete();
    };

    generate();
    // eslint-disable-next-line
  }, [data]);

  // Off-screen, but laid out with real dimensions so html2canvas can capture it.
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        width: RENDER_WIDTH,
        background: '#ffffff',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    >
      {data && (
        <div ref={captureRef} style={{ width: RENDER_WIDTH, background: '#fff' }}>
          <SessionDetails
            printInfoData={data.printInfoData}
            printMoreData={data.printMoreData?.medDiagonsis}
            patietSection={data.patietSection}
            coverages={data.coverages}
            contacts={data.contacts}
            allergies={data.allergies}
            advanceConsents={data.advanceConsents}
            practitioners={data.practitioners}
            loading={false}
            jobName={data.jobName}
            cptCodesInContent={data.printInfoData?.[0]?.cptCodesInContent}
          />
        </div>
      )}
    </div>
  );
};

export default FaceSheetPdf;
